import React, { useEffect, useMemo, useRef, useState } from 'react'

import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'

import { Controller, useFormContext } from 'react-hook-form'
import { Label } from 'components'

import useFetch from 'hooks/useFetch'
import * as service from 'service'

const ResponsibleUsersSelector = ({
  dataProcessId,
  initialOtherDescription,
}) => {
  const { setValue, errors, control, watch } = useFormContext()
  const [populatedId, setPopulatedId] = useState(null)
  const departmentId = watch('departmentId')
  const previousDepartmentId = useRef(departmentId)
  const responsibleUsers = watch('responsibleUsers') || []
  const hasOther = responsibleUsers.some((user) => user.id === 0)

  const {
    response: responseDepartmentUsers,
    loading: departmentUsersIsLoading,
  } = useFetch(
    service.dponet.departments.listUsers,
    { departmentId },
    [departmentId],
    !!departmentId,
  )

  const userList = useMemo(
    () => responseDepartmentUsers?.data?.users || [],
    [responseDepartmentUsers],
  )

  const {
    response: responseResponsibleUsers,
    loading: loadingResponsibleUsers,
  } = useFetch(
    service.dponet.dataProcesses.getProcessResponsibles,
    { dataProcessId },
    [dataProcessId],
    !!dataProcessId,
  )

  useEffect(() => {
    if (
      loadingResponsibleUsers ||
      !responseResponsibleUsers ||
      populatedId === dataProcessId
    )
      return
    const responsibleUsers =
      responseResponsibleUsers.data?.dataProcessResponsibles || []
    const initialResponsibles = responsibleUsers.map((item) => {
      const responsible = item.responsible || {}
      return {
        id: responsible.id,
        name: responsible.name || 'Usuário Sem Nome',
      }
    })
    if (initialOtherDescription) {
      setValue('otherAssigneesDescription', initialOtherDescription)
      if (
        !initialResponsibles.some(
          (responsible) => String(responsible.id) === '0',
        )
      ) {
        initialResponsibles.push({ id: 0, name: 'Outros' })
      }
    }
    if (initialResponsibles.length > 0 || initialOtherDescription) {
      setValue('responsibleUsers', initialResponsibles)
    }
    setPopulatedId(dataProcessId)
  }, [
    responseResponsibleUsers,
    loadingResponsibleUsers,
    initialOtherDescription,
    setValue,
    dataProcessId,
    populatedId,
  ])

  useEffect(() => {
    if (departmentUsersIsLoading) return
    const isNewDepartment = previousDepartmentId.current !== departmentId
    if (isNewDepartment) {
      setValue('responsibleUsers', [])
      setValue('otherAssigneesDescription', '')
    } else {
      const userIds = new Set(userList.map((user) => user.id))
      const newValue = responsibleUsers.filter(
        (user) => user.id === 0 || userIds.has(user.id),
      )
      if (newValue.length !== responsibleUsers.length) {
        setValue('responsibleUsers', newValue)
      }
    }
    previousDepartmentId.current = departmentId
  }, [
    departmentId,
    departmentUsersIsLoading,
    userList,
    responsibleUsers,
    setValue,
  ])

  return (
    <>
      <Label
        title="Responsáveis pelo processo"
        description="Selecione quais são os usuários responsáveis por este processo. Caso o responsável não esteja cadastrado na plataforma, utilize a opção 'Outros'"
        item
        xs={12}
      >
        <Controller
          as={
            <Autocomplete
              multiple
              fullWidth
              filterSelectedOptions
              loading={departmentUsersIsLoading}
              options={[{ id: 0, name: 'Outros' }, ...userList]}
              getOptionLabel={(option) => option.name || ''}
              getOptionSelected={(option, value) =>
                String(option.id) === String(value.id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!errors.responsibleUsers}
                  helperText={
                    errors?.responsibleUsers
                      ? 'Selecione os responsáveis pelo processo'
                      : ''
                  }
                  label="Selecione os responsáveis pelo processo"
                  variant="outlined"
                />
              )}
            />
          }
          name="responsibleUsers"
          control={control}
          defaultValue={[]}
          onChange={([, data]) => {
            if (!data.some((u) => u.id === 0)) {
              setValue('otherAssigneesDescription', '')
            }
            return data
          }}
        />
      </Label>

      {hasOther && (
        <Label
          title="Nome do responsável"
          description="Informe o nome do responsável"
          item
          xs={12}
        >
          <Controller
            as={
              <TextField
                fullWidth
                label="Nome do responsável"
                variant="outlined"
              />
            }
            name="otherAssigneesDescription"
            control={control}
            defaultValue={initialOtherDescription || ''}
          />
        </Label>
      )}
    </>
  )
}

export default ResponsibleUsersSelector
